import { memo } from "react";
import { Tooltip } from "antd";
import { InfoCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import "./calendario.css";

/** Identidad visual de cada fila meta. Cada tono tiene su propio color/estilo. */
export type MetaTone = "base" | "condicion" | "efectivo" | "minnoches" | "restriccion";

type Props = {
  label: string;
  dates: string[];
  /**
   * Columnas de la grilla de días, el mismo string que usa la cabecera de
   * fechas (CalendarioGrid: 56 px por día en el celular, 72 px en escritorio).
   * Hasta el round 2026-09-15 la fila fijaba 72 px por su cuenta y en el
   * celular cada día quedaba 16 px más a la derecha que su cabecera.
   */
  gridTemplateColumns: string;
  values: Record<string, any>;
  clickable?: boolean;
  onCellClick?: (date: string) => void;
  highlightRange?: [string, string] | null;
  /** Texto de ayuda (i) junto al label. */
  info?: string;
  /** Formateo del valor de cada celda (ej. CLP). Por defecto muestra el valor crudo. */
  format?: (value: any) => React.ReactNode;
  /** Tono/identidad visual de la fila (color y render del valor). */
  tone?: MetaTone;
  /**
   * true mientras la meta de la propiedad se está refrescando tras un cambio
   * (PATCH aplicado, esperando la re-lectura con los valores definitivos).
   * Muestra un spinner en el label y atenúa/pulsa las celdas — los valores
   * visibles aún son los VIEJOS. Round 2026-07-02.
   */
  refreshing?: boolean;
};

/**
 * Un valor de 9 caracteres o más (ej. «$1.250.000», «−$150.000») no cabe con
 * la letra normal: la celda lleva `.cal-meta-cell--largo`, que la achica.
 */
function esValorLargo(display: React.ReactNode): boolean {
  return (
    (typeof display === "string" || typeof display === "number") &&
    String(display).length > 8
  );
}

function isInRange(date: string, range: [string, string] | null | undefined) {
  if (!range) return false;
  const [start, end] = range[0] <= range[1] ? range : [range[1], range[0]];
  return date >= start && date <= end;
}

/** Render del valor de una celda según el tono (color por signo, píldoras, énfasis). */
function renderToneValue(display: React.ReactNode, raw: any, tone?: MetaTone): React.ReactNode {
  if (display === "-" || display == null) return <span className="cal-meta-dash">–</span>;

  if (tone === "condicion") {
    const s = String(raw);
    // "+7%" adición (verde) · "-10%" / "−$5.000" promoción (rojo).
    const isPos = s.trim().startsWith("+");
    const isNeg = s.trim().startsWith("-") || s.trim().startsWith("−");
    const cls = isPos ? "cal-cond-val cal-cond-pos" : isNeg ? "cal-cond-val cal-cond-neg" : "cal-cond-val";
    return <span className={cls}>{display}</span>;
  }

  if (tone === "restriccion") {
    // "IN", "OUT", "IN OUT" → una píldora por token.
    const tokens = String(raw).toUpperCase().split(/\s+/).filter(Boolean);
    return (
      <span className="cal-restr-wrap">
        {tokens.map((t, i) => (
          <span key={i} className={`cal-restr-pill cal-restr-${t.toLowerCase()}`}>
            {t}
          </span>
        ))}
      </span>
    );
  }

  if (tone === "efectivo") return <span className="cal-efectivo-val">{display}</span>;
  if (tone === "base") return <span className="cal-base-val">{display}</span>;
  return display;
}

function CalendarioMetaRow({
  label,
  dates,
  values,
  clickable,
  onCellClick,
  highlightRange,
  info,
  format,
  tone,
  refreshing,
  gridTemplateColumns,
}: Props) {
  // Clases de tono POR ELEMENTO: el label usa `.cal-meta-label--<tono>` y la
  // celda `.cal-meta-cell--<tono>` (así lo definen las reglas en calendario.css).
  // Antes se emitía un único `cal-meta--<tono>` que no matcheaba ninguna → el
  // acento del label y el tinte de celda no se pintaban. Fix 2026-07-12.
  const labelToneCls = tone ? ` cal-meta-label--${tone}` : "";
  const cellToneCls = tone ? ` cal-meta-cell--${tone}` : "";
  const refreshCls = refreshing ? " cal-meta--refreshing" : "";

  return (
    <>
      <Tooltip
        title={refreshing ? "Actualizando en Stays…" : info || label}
        mouseEnterDelay={info ? 0.3 : 1}
      >
        <div className={`cal-prop cal-meta-label cal-sticky-col${labelToneCls}${refreshCls}`}>
          <span className="cal-meta-label-accent" aria-hidden />
          <span className="cal-meta-label-text">{label}</span>
          {refreshing ? (
            <LoadingOutlined style={{ marginLeft: 6, fontSize: 12, color: "var(--primary-color, #7c3aed)" }} />
          ) : info ? (
            <InfoCircleOutlined style={{ marginLeft: 6, opacity: 0.55, fontSize: 12 }} />
          ) : null}
        </div>
      </Tooltip>

      <div className="cal-row-dates">
        <div className={`cal-dates-grid${refreshCls}`} style={{ gridTemplateColumns }}>
          {dates.map((date) => {
            const raw = values?.[date];
            const display = raw == null || raw === "" ? "-" : format ? format(raw) : raw;
            const largoCls = esValorLargo(display) ? " cal-meta-cell--largo" : "";
            return (
              <div
                key={date}
                className={`cal-meta-cell${cellToneCls}${largoCls}${clickable && !refreshing ? " cal-meta-cell--clickable" : ""}${isInRange(date, highlightRange) ? " cal-meta-cell--highlight" : ""}`}
                onClick={clickable && !refreshing ? () => onCellClick?.(date) : undefined}
              >
                {renderToneValue(display, raw, tone)}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

// Memoizada (round 2026-09-11): con rangos de hasta 14 meses cada fila meta son
// ~427 celdas. CalendarioGrid le pasa handlers y rango destacado estables, así
// que solo se vuelve a renderizar cuando cambian sus valores, su draft o su
// estado de refresco.
export default memo(CalendarioMetaRow);
